import type { SupabaseClient } from "@supabase/supabase-js";

// Alert Queue read model — public.management_alert_queue (management-scoped; guard_label is already
// sha-redacted server-side; no raw guard ids/coordinates). Plus the acknowledge mutation, which
// resolves an open alert via the SECURITY DEFINER RPC public.acknowledge_alert_event (account
// scoping + authorization enforced server-side; the client only passes ids through).

export interface AlertRow {
  alertEventId: string;
  alertType: string;
  severity: string;
  status: string;
  routeAssignmentId: string | null;
  siteId: string | null;
  siteAssetId: string | null;
  routeName: string | null;
  siteName: string | null;
  assetName: string | null;
  checkpointName: string | null;
  guardLabel: string | null;
  dueAt: string | null;
  detectedAt: string | null;
  acknowledgedAt: string | null;
  /** The human sentence written when the alert was acknowledged. Already selected by `*`. */
  acknowledgeNote: string | null;
  /** Closed SOS stand-down outcome when present. Null for non-SOS and pre-migration rows. */
  standDownOutcome: string | null;
  /** Supervisors who joined the live response. Zero when no one has Responded. */
  responseCount: number;
  /**
   * Duty-tracking last-known position for SOS only when the account policy allows a fresh
   * duty ping for that officer. Null means no duties location is available to show.
   */
  dutyLat: number | null;
  dutyLng: number | null;
  dutyGpsAccuracyMeters: number | null;
  dutyPositionAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** "missed_checkpoint" → "Missed checkpoint". Safe for any string. */
export function humanizeLabel(value: string): string {
  const cleaned = value.replace(/[_-]+/g, " ").trim();
  if (cleaned.length === 0) return value;
  return cleaned.slice(0, 1).toUpperCase() + cleaned.slice(1);
}

/** SOS is its own life-safety class — render an uppercase "SOS" badge, not humanized "Sos". */
export function isSosAlert(alertType: string): boolean {
  return alertType === "sos";
}

export function isSiteGeofenceAlert(alertType: string): boolean {
  return (
    alertType === "left_site_boundary" ||
    alertType === "entered_site_boundary" ||
    alertType === "left_asset_zone" ||
    alertType === "entered_asset_zone"
  );
}

export function isAssetZoneAlert(alertType: string): boolean {
  return alertType === "left_asset_zone" || alertType === "entered_asset_zone";
}

/** Welfare signal: guard left site boundary or an L1 asset zone while on duty. */
export function isGeofenceExitAlert(alertType: string): boolean {
  return alertType === "left_site_boundary" || alertType === "left_asset_zone";
}

export function alertSupportsMapFocus(
  alert: Pick<AlertRow, "alertType" | "routeAssignmentId" | "siteId">,
): boolean {
  if (!isSiteGeofenceAlert(alert.alertType)) {
    return false;
  }
  return (
    (alert.routeAssignmentId !== null && alert.routeAssignmentId.length > 0) ||
    (alert.siteId !== null && alert.siteId.length > 0)
  );
}

/** Display label for an alert type: "SOS" for sos, humanized otherwise (e.g. "Missed checkpoint"). */
export function formatAlertType(alertType: string): string {
  if (isSosAlert(alertType)) {
    return "SOS";
  }
  if (alertType === "left_site_boundary") {
    return "Left site";
  }
  if (alertType === "entered_site_boundary") {
    return "Entered site";
  }
  if (alertType === "left_asset_zone") {
    return "Left asset zone";
  }
  if (alertType === "entered_asset_zone") {
    return "Entered asset zone";
  }
  return humanizeLabel(alertType);
}

export function formatAlertLocation(
  alert: Pick<AlertRow, "alertType" | "assetName" | "checkpointName" | "routeName" | "siteName">,
): string {
  if (isAssetZoneAlert(alert.alertType)) {
    const parts = [alert.assetName ?? alert.siteName ?? "Asset zone"];
    if (alert.siteName && alert.assetName && alert.siteName !== alert.assetName) {
      parts.unshift(alert.siteName);
    }
    if (alert.routeName) {
      parts.push(alert.routeName);
    }
    return parts.join(" · ");
  }
  if (isSiteGeofenceAlert(alert.alertType)) {
    const parts = [alert.siteName ?? "Site boundary"];
    if (alert.routeName) {
      parts.push(alert.routeName);
    }
    return parts.join(" · ");
  }
  const parts = [alert.checkpointName ?? "Checkpoint"];
  if (alert.routeName) {
    parts.push(alert.routeName);
  }
  if (alert.siteName) {
    parts.push(alert.siteName);
  }
  return parts.join(" · ");
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asCount(value: unknown): number {
  const n = asNumber(value);
  if (n === null) return 0;
  return Math.max(0, Math.floor(n));
}

export function mapAlertRow(raw: Record<string, unknown>): AlertRow {
  return {
    alertEventId: asString(raw.alert_event_id) ?? "",
    alertType: asString(raw.alert_type) ?? "alert",
    severity: asString(raw.severity) ?? "warning",
    status: asString(raw.status) ?? "open",
    routeAssignmentId: asString(raw.route_assignment_id),
    siteId: asString(raw.site_id),
    siteAssetId: asString(raw.site_asset_id),
    routeName: asString(raw.route_name),
    siteName: asString(raw.site_name),
    assetName: asString(raw.asset_name),
    checkpointName: asString(raw.checkpoint_name),
    guardLabel: asString(raw.guard_label),
    dueAt: asString(raw.due_at),
    detectedAt: asString(raw.detected_at),
    acknowledgedAt: asString(raw.acknowledged_at),
    acknowledgeNote: asString(raw.acknowledge_note),
    standDownOutcome: asString(raw.stand_down_outcome),
    responseCount: asCount(raw.response_count),
    dutyLat: asNumber(raw.duty_lat),
    dutyLng: asNumber(raw.duty_lng),
    dutyGpsAccuracyMeters: asNumber(raw.duty_gps_accuracy_meters),
    dutyPositionAt: asString(raw.duty_position_at),
  };
}

const ALERT_QUEUE_LIMIT = 50;

/** Fetch the account's alert queue, newest first. Throws on read error (caller shows error state). */
export async function fetchAlertQueue(
  client: SupabaseClient,
  accountId: string,
  limit: number = ALERT_QUEUE_LIMIT,
): Promise<AlertRow[]> {
  const { data, error } = await client
    .from("management_alert_queue")
    .select("*")
    .eq("account_id", accountId)
    .order("detected_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load alerts.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapAlertRow(row as Record<string, unknown>));
}

/** Single SAFE generic failure for the acknowledge action — never leaks the RPC error or an id. */
export const SAFE_ACK_FAILURE = "Couldn't acknowledge that alert — please try again.";

export type AcknowledgeResult = { ok: true } | { ok: false; reason: string };

/**
 * Acknowledge (resolve) an open alert via public.acknowledge_alert_event. Authorization and account
 * scoping are enforced SERVER-SIDE by the SECURITY DEFINER RPC + RLS — this only passes the account
 * + alert ids through the authenticated client (no service-role, no raw SQL). Any failure returns
 * the SAFE generic reason; the raw RPC error is never surfaced, and the alert id is the operation
 * target only (never displayed back to the user).
 */
export async function acknowledgeAlert(
  client: SupabaseClient,
  accountId: string,
  alertEventId: string,
  note?: string,
): Promise<AcknowledgeResult> {
  const trimmedNote = typeof note === "string" && note.trim().length > 0 ? note.trim() : null;
  try {
    const { error } = await client.rpc("acknowledge_alert_event", {
      p_account_id: accountId,
      p_alert_event_id: alertEventId,
      p_note: trimmedNote,
    });
    if (error !== null) {
      return { ok: false, reason: SAFE_ACK_FAILURE };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_ACK_FAILURE };
  }
}
